import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { manutencaoService } from '../services/api';
import { formatCurrency, formatDate } from '../utils/format';
import { 
  WrenchScrewdriverIcon, 
  CurrencyDollarIcon, 
  CheckCircleIcon, 
  ExclamationTriangleIcon,
  CalendarIcon,
  ClockIcon,
  XCircleIcon
} from '@heroicons/react/24/outline';

const RelatorioManutencoes = ({ veiculoId, veiculoInfo }) => {
  const [filtros, setFiltros] = useState({
    page: 1,
    limit: 10
  });

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['manutencoes-veiculo', veiculoId, filtros],
    queryFn: () => manutencaoService.porVeiculo(veiculoId, filtros),
    enabled: !!veiculoId
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'agendada': return 'bg-yellow-100 text-yellow-800';
      case 'em_andamento': return 'bg-blue-100 text-blue-800';
      case 'concluida': return 'bg-green-100 text-green-800';
      case 'cancelada': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'agendada': return <CalendarIcon className="w-4 h-4" />;
      case 'em_andamento': return <ClockIcon className="w-4 h-4" />;
      case 'concluida': return <CheckCircleIcon className="w-4 h-4" />;
      case 'cancelada': return <XCircleIcon className="w-4 h-4" />;
      default: return <WrenchScrewdriverIcon className="w-4 h-4" />;
    }
  };

  const getTipoColor = (tipo) => {
    switch (tipo) {
      case 'emergencial': return 'bg-red-100 text-red-800';
      case 'preventiva': return 'bg-green-100 text-green-800';
      case 'corretiva': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-400" />
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800">
              Erro ao carregar manutenções
            </h3>
            <div className="mt-2 text-sm text-red-700">
              {error.response?.data?.message || 'Erro interno do servidor'}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { manutencoes, estatisticas, pagination } = data?.data || {};

  return (
    <div className="space-y-6">
      {/* Cabeçalho */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Relatório de Manutenções
            </h2>
            <p className="text-gray-600 mt-1">
              {veiculoInfo?.placa} - {veiculoInfo?.marca} {veiculoInfo?.modelo}
            </p>
          </div>
          <button
            onClick={() => refetch()}
            className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
          >
            Atualizar
          </button>
        </div>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <WrenchScrewdriverIcon className="h-8 w-8 text-purple-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Total de Manutenções</p>
              <p className="text-2xl font-bold text-gray-900">
                {estatisticas?.totalManutencoes || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CurrencyDollarIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Custo Total</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(estatisticas?.totalCustos || 0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <CheckCircleIcon className="h-8 w-8 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Concluídas</p>
              <p className="text-2xl font-bold text-gray-900">
                {estatisticas?.manutencoesConcluidas || 0}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-500">Emergenciais</p>
              <p className="text-2xl font-bold text-gray-900">
                {estatisticas?.manutencoesEmergenciais || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de Manutenções */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Histórico de Manutenções
          </h3>
        </div>

        {manutencoes?.length === 0 ? (
          <div className="p-6 text-center">
            <WrenchScrewdriverIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              Nenhuma manutenção encontrada
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Este veículo ainda não possui manutenções registradas.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {manutencoes?.map((manutencao) => (
              <div key={manutencao._id} className="p-6 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(manutencao.status)}`}>
                        {getStatusIcon(manutencao.status)}
                        <span className="ml-1 capitalize">
                          {manutencao.status.replace('_', ' ')}
                        </span>
                      </span>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTipoColor(manutencao.tipo)}`}>
                        {manutencao.tipo.replace('_', ' ')}
                      </span>
                    </div>
                    
                    <h4 className="mt-2 text-lg font-medium text-gray-900">
                      {manutencao.descricao}
                    </h4>
                    
                    <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">Data:</span> {formatDate(manutencao.dataInicio)}
                      </div>
                      <div>
                        <span className="font-medium">Quilometragem:</span> {manutencao.quilometragem?.toLocaleString()} km
                      </div>
                      <div>
                        <span className="font-medium">Custo:</span> {formatCurrency(manutencao.custos?.total || 0)}
                      </div>
                    </div>

                    {manutencao.fornecedor?.nome && (
                      <div className="mt-2 text-sm text-gray-600">
                        <span className="font-medium">Fornecedor:</span> {manutencao.fornecedor.nome}
                      </div>
                    )}

                    {manutencao.veiculoSubstituto && (
                      <div className="mt-2 text-sm text-gray-600">
                        <span className="font-medium">Veículo Substituto:</span> {manutencao.veiculoSubstituto.placa}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Paginação */}
        {pagination && pagination.pages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Mostrando {((pagination.page - 1) * pagination.limit) + 1} a{' '}
                {Math.min(pagination.page * pagination.limit, pagination.total)} de{' '}
                {pagination.total} manutenções
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setFiltros(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={!pagination.hasPrevPage}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Anterior
                </button>
                <button
                  onClick={() => setFiltros(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={!pagination.hasNextPage}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Próxima
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RelatorioManutencoes;







